import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaItems } from "@/mocks/media";
import { specialItems } from "@/mocks/specials";
import type { MediaItem } from "@/types/content";

export default function Specials() {
  usePageMeta(
    "影视专题总览 - 天马传媒",
    "天马传媒影视专题总览，按主题聚合电影与剧集片单，涵盖经典科幻、城市悬疑与华语武侠，附推荐理由与关联资讯。",
    "影视专题,主题片单,天马传媒,科幻电影,悬疑剧集,武侠剧",
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200 bg-background-100">
          <div className="shell py-8 md:py-12">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800">影视专题</span>
            </nav>
            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                  影视专题
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  按主题而非类型来组织片单。每个专题包含作品聚合、推荐理由与相关资讯，适合一次看完一整组内容。
                </p>
              </div>
              <span className="w-fit rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-500">
                共 {specialItems.length} 个专题
              </span>
            </div>
          </div>
        </section>

        <div className="shell space-y-6 py-10 md:space-y-8 md:py-12">
          {specialItems.map((special, index) => {
            const works = special.mediaIds
              .map((mediaId) => mediaItems.find((item) => item.id === mediaId))
              .filter((item): item is MediaItem => Boolean(item));

            return (
              <article
                key={special.id}
                className="grid gap-5 overflow-hidden rounded-lg border border-background-200 bg-background-50 md:grid-cols-[1.15fr_1fr]"
              >
                <Link
                  to={`/special/${special.id}`}
                  className="group relative block overflow-hidden bg-primary-900"
                >
                  <div className="h-56 w-full md:h-full md:min-h-[280px]">
                    <img
                      src={special.cover}
                      alt={`${special.title}专题封面`}
                      title={`${special.title} - 天马传媒影视专题`}
                      className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent"></div>
                  <span className="absolute left-4 top-4 rounded bg-background-50/90 px-2 py-1 text-[10px] tracking-widest text-primary-800">
                    SPECIAL {String(index + 1).padStart(2, "0")}
                  </span>
                </Link>

                <div className="flex flex-col justify-between p-5 md:p-6">
                  <div>
                    <div className="flex flex-wrap gap-1.5">
                      {special.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded border border-secondary-100 bg-secondary-50 px-2 py-0.5 text-[10px] text-secondary-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950">
                      <Link
                        to={`/special/${special.id}`}
                        className="cursor-pointer transition hover:text-primary-600"
                      >
                        {special.title}
                      </Link>
                    </h2>
                    <p className="mt-1 text-xs text-accent-700">{special.subtitle}</p>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                      {special.intro}
                    </p>
                  </div>

                  <div className="mt-5 flex items-center justify-between gap-3 border-t border-background-200 pt-4">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {works.slice(0, 3).map((work) => (
                          <span
                            key={work.id}
                            className="h-8 w-8 overflow-hidden rounded-full border-2 border-background-50 bg-primary-900"
                          >
                            <img
                              src={work.poster}
                              alt={`${work.title}海报缩略图`}
                              title={`${work.title} - 天马传媒`}
                              className="h-full w-full object-cover object-top"
                            />
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-foreground-500">{works.length} 部作品</span>
                    </div>
                    <Link
                      to={`/special/${special.id}`}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition hover:bg-primary-600"
                    >
                      进入专题
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}